import { Box, Divider, Popover, Typography } from "@mui/material";
import useColors from "../../../../../api/hooks/useColors";
import { ReactComponent as StepsIcon } from "../../../../../icons/icon_files/Attack-steps_24.svg";
import { ReactComponent as ControlsIcon } from "../../../../../icons/icon_files/Controls_24.svg";
import { ReactComponent as CriticalAsset } from "../../../../../icons/icon_files/Critical-asset_24.svg";
import { ReactComponent as VulnerabilitiesIcon } from "../../../../../icons/icon_files/Vulnerabilities_24.svg";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { EntityRisk } from "../../../../../utils/constants";
import ChipColor from "../../../../remediation/plan/components/ChipColor";

const getEntityRiskBreakdownInfo = (entity) => {
  let gapText;
  let gapEnabled;

  let cveText;
  let cveEnabled;

  let stepText;
  let stepEnabled;

  if (entity?.risk === EntityRisk.MINIMAL) {
    const hasGaps = [
      entity?.risk_breakdown?.gaps_high,
      entity?.risk_breakdown?.gaps_medium,
      entity?.risk_breakdown?.gaps_low,
    ].some(Boolean);

    const hasCVEs = [
      entity?.risk_breakdown?.vulns_high,
      entity?.risk_breakdown?.vulns_medium,
      entity?.risk_breakdown?.vulns_low,
    ].some(Boolean);

    gapText = "No gaps";
    gapEnabled = !hasGaps;

    cveText = "No CVEs";
    cveEnabled = !hasCVEs;

    stepText = "No active steps";
    stepEnabled = !entity?.risk_breakdown?.steps;
  } else {
    const hasHighGaps = entity?.risk_breakdown?.gaps_high;
    const hasMediumLowGaps =
      entity?.risk_breakdown?.gaps_medium || entity?.risk_breakdown?.gaps_low;
    const hasHighCVEs =
      entity?.risk_breakdown?.vulns_critical ||
      entity?.risk_breakdown?.vulns_high;
    const hasMediumLowCVEs =
      entity?.risk_breakdown?.vulns_medium || entity?.risk_breakdown?.vulns_low;

    gapEnabled = false;
    gapText = "At least one high severity gap";

    cveEnabled = false;
    cveText = "At least one high severity CVE";

    stepText = "At least one active step";
    stepEnabled = entity?.risk_breakdown?.steps ? true : false;

    if (hasHighGaps) gapEnabled = true;
    else if (
      ![EntityRisk.CRITICAL, EntityRisk.HIGH].includes(entity?.risk) &&
      hasMediumLowGaps
    ) {
      gapText = "At least one low/medium severity gap";
      gapEnabled = true;
    }

    if (hasHighCVEs) cveEnabled = true;
    else if (hasMediumLowCVEs) {
      cveText = "At least one low/medium severity CVE";
      cveEnabled = true;
    }
  }

  return [gapText, gapEnabled, cveText, cveEnabled, stepText, stepEnabled];
};

export default function EntityRiskBreakdown({ entity, anchorEl, setAnchorEl }) {
  const colors = useColors();

  const [gapText, gapEnabled, cveText, cveEnabled, stepText, stepEnabled] =
    getEntityRiskBreakdownInfo(entity);

  const breakdowns = [
    {
      icon: ControlsIcon,
      text: gapText,
      enabled: gapEnabled,
    },
    {
      icon: VulnerabilitiesIcon,
      text: cveText,
      enabled: cveEnabled,
    },
    {
      icon: StepsIcon,
      text: stepText,
      enabled: stepEnabled,
    },
    {
      icon: CriticalAsset,
      text: "Included in a critical asset",
      enabled: entity?.risk_breakdown?.assets,
    },
  ];

  return (
    <Popover
      open={Boolean(anchorEl)}
      onClose={() => setAnchorEl(null)}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      sx={{
        ".MuiPopover-paper	": {
          display: "flex",
          flexDirection: "column",
          boxShadow: "1px 1px 8.5px 3px  rgba(203, 203, 203, 0.50)",
          borderRadius: "10px",
          padding: "16px",
          width: "content-max",
        },
      }}
    >
      <Box display="flex" flexDirection="column" gap={"10px"}>
        <Box height="24px">
          <ChipColor
            value={entity?.risk}
            type="entity_risk"
            style={{
              width: "100px",
              height: "25px",
            }}
          />
        </Box>
        <Divider />
      </Box>
      {breakdowns.map((breakdown, index) => (
        <Box
          key={index}
          display="flex"
          flexDirection="column"
          paddingTop={index === 0 ? "4px" : "0"}
        >
          <Box height="36px" display="flex" alignItems="center" gap={1.5}>
            <SvgToIcon
              IconLogo={breakdown.icon}
              style={{
                color: breakdown.enabled
                  ? colors.grey_700
                  : colors.action_disabled,
              }}
            />
            <Typography
              variant="overline"
              color={!breakdown.enabled ? colors.text_disabled : null}
            >
              {breakdown.text}
            </Typography>
          </Box>
        </Box>
      ))}
    </Popover>
  );
}
